import { useMemo, useState } from 'react'
import { Badge, Button, Group, Paper, ScrollArea, Stack, Tabs, Text, Title } from '@mantine/core'
import { IconLayoutList, IconNews, IconPlus } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import { useAnnouncements } from '@features/communications/queries'
import { AnnouncementEditorModal } from '@features/communications/components/AnnouncementEditorModal'
import { StudentFeedNotificationSettings } from '@features/communications/components/StudentFeedNotificationSettings'
import { AppDataTable, createAppColumnHelper, useAppTable } from '@features/data-table'
import { StudentFeed } from '@features/student-announcements/components/StudentFeed'
import type { AnnouncementSummary } from '@features/communications/types'

const columnHelper = createAppColumnHelper<AnnouncementSummary>()

export const Route = createFileRoute('/_layout/communication/feed')({
    component: AnnouncementsPage,
    staticData: { crumb: { label: 'Feed' } },
})

function AnnouncementsPage() {
    const [view, setView] = useState<string | null>('table')
    const [composerOpen, setComposerOpen] = useState(false)
    const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(null)
    const announcementsQuery = useAnnouncements()
    const rows = useMemo(() => announcementsQuery.data ?? [], [announcementsQuery.data])
    const columns = useMemo(() => columnHelper.columns([
        columnHelper.accessor('title', { header: 'Title', size: 300, meta: { label: 'Title', truncate: true } }),
        columnHelper.accessor(row => row.audiences.map(audience => audience.label).join(', '), { id: 'audience', header: 'Audience', size: 260, meta: { label: 'Audience', truncate: true } }),
        columnHelper.accessor(row => row.displayAuthor ?? '', { id: 'author', header: 'Author', size: 170, meta: { label: 'Author' } }),
        columnHelper.accessor('status', { header: 'Status', size: 120, meta: { label: 'Status', variant: 'select' }, cell: context => <Badge variant="light">{context.getValue()}</Badge> }),
        columnHelper.accessor('createdAt', { header: 'Created', size: 190, meta: { label: 'Created', variant: 'date' }, cell: context => new Date(context.getValue()).toLocaleString() }),
    ]), [])
    const table = useAppTable({ tableKey: 'communication-announcements', data: rows, columns, getRowId: row => row.id, enableSorting: true, enableHiding: true, enableColumnResizing: true, enableColumnPinning: true, initialState: { sorting: [{ id: 'createdAt', desc: true }] } })
    return (
        <Stack p="lg" gap="lg" h="100%" mih={0}>
            <Group justify="space-between" align="flex-start">
                <div>
                    <Title order={2}>Feed</Title>
                    <Text c="dimmed">Create and manage communications published to the Student Portal.</Text>
                </div>
                <Group>
                    <StudentFeedNotificationSettings manager />
                    <Button leftSection={<IconPlus size={16} />} onClick={() => { setSelectedAnnouncementId(null); setComposerOpen(true) }}>New post</Button>
                </Group>
            </Group>

            <Tabs value={view} onChange={setView} style={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'column' }}>
                <Tabs.List>
                    <Tabs.Tab value="table" leftSection={<IconLayoutList size={16} />}>Table</Tabs.Tab>
                    <Tabs.Tab value="feed" leftSection={<IconNews size={16} />}>Feed</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="table" pt="lg">
                    <AppDataTable table={table} tableKey="communication-announcements" loading={announcementsQuery.isPending} error={announcementsQuery.isError ? 'Feed posts could not be loaded.' : undefined} emptyState="No posts have been created yet." toolbarOptions={{ search: true, filter: true, sort: true, view: true }} onRowClick={openAnnouncement} minWidth={1050} />
                </Tabs.Panel>

                <Tabs.Panel value="feed" pt="lg" style={{ flex: 1, minHeight: 0 }}>
                    <Paper withBorder radius="md" h="100%" style={{ overflow: 'hidden' }}>
                        <ScrollArea h="100%" type="auto" offsetScrollbars="y">
                            <StudentFeed />
                        </ScrollArea>
                    </Paper>
                </Tabs.Panel>
            </Tabs>

            <AnnouncementEditorModal opened={composerOpen} announcementId={selectedAnnouncementId} onClose={() => setComposerOpen(false)} />
        </Stack>
    )

    function openAnnouncement(announcement: AnnouncementSummary) {
        setSelectedAnnouncementId(announcement.id)
        setComposerOpen(true)
    }
}
